import { useState } from 'react'

import { useToast } from '@/hooks'
import { MAX_ATTACHMENT_BYTES, MAX_ATTACHMENT_COUNT } from '../attachments'

const sameFile = (first: File, second: File) =>
  first.name === second.name &&
  first.size === second.size &&
  first.lastModified === second.lastModified

// Callers that outlive one thread pass its id as the reset key, so files picked
// for one ticket cannot be sent to the next one.
export const usePendingAttachments = (resetKey?: string) => {
  const [files, setFiles] = useState<File[]>([])
  const [pickedFor, setPickedFor] = useState(resetKey)
  const { errorToast } = useToast()

  if (pickedFor !== resetKey) {
    setPickedFor(resetKey)
    setFiles([])
  }

  // A picker and a drop hand over a FileList; a discarded reply hands back the
  // files it was carrying.
  const addFiles = (picked: FileList | File[] | null) => {
    if (!picked) return
    const incoming = Array.from(picked)
    const accepted = incoming.filter(
      (file) => file.size <= MAX_ATTACHMENT_BYTES,
    )
    for (const oversized of incoming.filter(
      (file) => file.size > MAX_ATTACHMENT_BYTES,
    )) {
      errorToast('File too large', `${oversized.name} is over 25 MB.`)
    }
    if (accepted.length === 0) return
    setFiles((current) => {
      const fresh = accepted.filter(
        (candidate) =>
          !current.some((existing) => sameFile(existing, candidate)),
      )
      const combined = [...current, ...fresh]
      if (combined.length > MAX_ATTACHMENT_COUNT) {
        errorToast(
          'Too many files',
          `You can attach up to ${MAX_ATTACHMENT_COUNT} files per message.`,
        )
        return current
      }
      return combined
    })
  }

  const removeFile = (index: number) =>
    setFiles((current) =>
      current.filter((_file, position) => position !== index),
    )

  const clearFiles = () => setFiles([])

  return { files, addFiles, removeFile, clearFiles }
}

export type PendingAttachments = ReturnType<typeof usePendingAttachments>
